import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from '../service/auth/auth.service';
import { GoalSetup, RestService } from '../service/rest/rest.service';
import { ContentPackService } from '../service/content-pack/content-pack.service';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { LoggerService } from '../service/utils/logger.service';
import { RetentionFeatureFlagsService } from '../service/retention/retention-feature-flags.service';
import { BrandService } from '../service/brand/brand.service';
import {
  DASHBOARD_COURSE_STORAGE_SUFFIX,
  parseStoredCourseId,
  pickPreferredCourseId,
} from '../dashboard/dashboard-course-preference';

interface QuestionSeed {
  id: number;
  name: string;
  createdAt: string;
}

interface Course {
  id: number;
  name: string;
}

interface SubjectRef {
  id: number;
  name: string;
}

type PackState = 'pending' | 'downloading' | 'importing' | 'installed' | 'failed' | 'unknown';

interface DownloadedPackRow {
  courseId: number;
  subjectId: number;
  subjectName: string;
  questionCount: number;
  payloadBytes: number;
  sizeLabel: string;
  state: PackState;
  stateLabel: string;
  stateBadgeClass: string;
  attemptCount: number;
  lastError: string | null;
  contentVersion: string | null;
  isInstalled: boolean;
}

type SettingsTab = 'learning' | 'schedule' | 'reminders' | 'tests' | 'data';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit, OnDestroy {
  activeTab: SettingsTab = 'learning';
  settingsForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  isResetting = false;
  showResetConfirm = false;
  showClearConfirm = false;

  questionSeeds: QuestionSeed[] = [];
  courses: Course[] = [];
  learningGoalCourses: Course[] = [];
  subjects: SubjectRef[] = [];

  selectedCourseId: number | null = null;
  selectedSubjectId: number | null = null;
  learningGoalCourseId: number | null = null;
  goalSetup: GoalSetup | null = null;
  goalTargetDate = '';
  customGoalDate = '';
  selectedGoalPresetId: string | null = null;
  leaderboardOptIn = false;
  weeklyTrainingDays = 7;
  includeWeekends = true;
  preferredSessionSize = 20;
  dailyMinutesTarget: number | null = null;
  reminderTimeLocal = '';
  reminderStyle: 'relaxed' | 'normal' | 'strict' = 'normal';
  notificationInApp = true;
  notificationPush = false;
  notificationEmail = false;
  notificationLocal = false;
  routineReminderEnabled = true;
  saveReminderEnabled = true;
  weeklySummaryEnabled = true;
  quietHoursStart = '';
  quietHoursEnd = '';
  isLoadingGoal = false;
  isSavingGoal = false;
  isSavingSchedule = false;
  isSavingReminders = false;
  goalMessage = '';
  goalMessageType: 'success' | 'error' = 'success';
  scheduleMessage = '';
  scheduleMessageType: 'success' | 'error' = 'success';
  reminderMessage = '';
  reminderMessageType: 'success' | 'error' = 'success';

  message: string = '';
  messageType: 'success' | 'error' = 'success';
  resetMessage: string = '';
  resetMessageType: 'success' | 'error' = 'success';
  isNative = isCapacitorNative();
  isClearingDownloads = false;
  offlinePackMessage = '';
  offlinePackMessageType: 'success' | 'error' = 'success';

  downloadedPacks: DownloadedPackRow[] = [];
  isLoadingDownloadedPacks = false;
  hasInstalledPacks = false;
  hasFailedPacks = false;
  isRetryingDownloads = false;
  totalDownloadedBytes = 0;
  totalDownloadedSizeLabel = '0 B';

  private autoSaveSub: Subscription | null = null;
  private suppressAutoSave = false;
  private packStatusTimer: ReturnType<typeof setInterval> | null = null;

  protected readonly retentionFlags = inject(RetentionFeatureFlagsService);
  protected readonly brand = inject(BrandService);

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private restService: RestService,
    private contentPackService: ContentPackService
  ) {
    this.initForm();
  }

  ngOnInit() {
    this.loadData();
    if (this.isNative) {
      this.autoSaveSub = this.settingsForm.valueChanges
        .pipe(debounceTime(400))
        .subscribe(() => {
          if (this.suppressAutoSave || this.isLoading) {
            return;
          }
          this.saveSettings(true);
        });
    }
  }

  ngOnDestroy() {
    this.autoSaveSub?.unsubscribe();
    this.stopPackStatusPolling();
  }

  initForm() {
    this.settingsForm = this.fb.group({
      seedId: [null],
      shuffleQuestionAnswers: [false],
      randomizeQuestions: [false],
      nextAutomatically: [false],
      autoDownloadLearningPacks: [this.contentPackService.isAutoDownloadEnabled()]
    });
  }

  async loadData() {
    this.isLoading = true;
    this.suppressAutoSave = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        LoggerService.error('User not authenticated', null);
        return;
      }
      await this.retentionFlags.ensureLoaded(user.sid);

      const settings = await this.restService.getUserTestSettings(user.sid);
      if (settings) {
        this.settingsForm.patchValue({
          seedId: settings.seedId || null,
          shuffleQuestionAnswers: settings.shuffleQuestionAnswers || false,
          randomizeQuestions: settings.randomizeQuestions || false,
          nextAutomatically: settings.nextAutomatically || false
        });
      }

      const seeds = await this.restService.getQuestionSeeds(user.sid);
      if (seeds) {
        this.questionSeeds = seeds;
      }

      const courses = await this.restService.getUserCourses(user.sid);
      if (courses) {
        this.courses = courses;
        if (courses.length > 0) {
          this.selectedCourseId = courses[0].id;
          await this.onCourseChange();
        }
      }

      const learningGoalCourses = await this.restService.getCoursePlanningOptions(user.sid);
      this.learningGoalCourses = learningGoalCourses.map((course) => ({
        id: course.id,
        name: course.trialStatus === 'expired' ? `${course.name} (trial expired)` : course.name,
      }));
      if (this.learningGoalCourses.length > 0) {
        this.learningGoalCourseId = pickPreferredCourseId(this.learningGoalCourses, {
          storedId: parseStoredCourseId(localStorage.getItem(this.brand.storageKey(DASHBOARD_COURSE_STORAGE_SUFFIX))),
          sessionCourseId: Number(this.restService.courseId) || null,
        });
        await this.loadLearningGoal();
      }

      if (this.isNative) {
        await this.loadDownloadedPacks();
      }
    } catch (error) {
      LoggerService.error('Error loading settings data:', error);
      this.showMessage('Failed to load settings', 'error');
    } finally {
      this.isLoading = false;
      setTimeout(() => { this.suppressAutoSave = false; }, 0);
    }
  }

  async loadDownloadedPacks() {
    if (!this.isNative) {
      this.downloadedPacks = [];
      this.hasInstalledPacks = false;
      this.totalDownloadedBytes = 0;
      this.totalDownloadedSizeLabel = '0 B';
      return;
    }
    this.isLoadingDownloadedPacks = true;
    try {
      const statuses = await this.contentPackService.listPackStatuses();
      const user = this.authService.getUser();
      const subjectNameByKey = new Map<string, string>();
      if (user?.sid && statuses.length > 0) {
        const courseIds = Array.from(new Set(statuses.map((s) => s.courseId)));
        for (const courseId of courseIds) {
          const subjects = await this.restService.getSubjectsForCourse(user.sid, courseId);
          for (const subj of subjects ?? []) {
            subjectNameByKey.set(`${courseId}:${subj.id}`, subj.name);
          }
        }
      }
      this.downloadedPacks = statuses.map((s) => ({
        courseId: s.courseId,
        subjectId: s.subjectId,
        subjectName: subjectNameByKey.get(`${s.courseId}:${s.subjectId}`) || `Subject #${s.subjectId}`,
        questionCount: s.questionCount,
        payloadBytes: s.payloadBytes,
        sizeLabel: s.payloadBytes > 0 ? this.formatBytes(s.payloadBytes) : '',
        state: s.state,
        stateLabel: this.labelForState(s.state),
        stateBadgeClass: this.badgeClassForState(s.state),
        attemptCount: s.attemptCount,
        lastError: s.lastError,
        contentVersion: s.contentVersion,
        isInstalled: s.state === 'installed' && s.hasManifest,
      }));
      this.hasInstalledPacks = this.downloadedPacks.some((p) => p.isInstalled);
      this.hasFailedPacks = this.downloadedPacks.some((p) => p.state === 'failed');
      this.totalDownloadedBytes = this.downloadedPacks.reduce((sum, p) => sum + p.payloadBytes, 0);
      this.totalDownloadedSizeLabel = this.formatBytes(this.totalDownloadedBytes);
      this.updatePackStatusPolling();
    } catch (error) {
      LoggerService.error('Error loading downloaded packs:', error);
      this.downloadedPacks = [];
      this.hasInstalledPacks = false;
      this.hasFailedPacks = false;
      this.totalDownloadedBytes = 0;
      this.totalDownloadedSizeLabel = '0 B';
    } finally {
      this.isLoadingDownloadedPacks = false;
    }
  }

  async retryFailedPacks() {
    if (!this.isNative || this.isRetryingDownloads) {
      return;
    }
    const failed = this.downloadedPacks.filter((p) => p.state === 'failed');
    if (failed.length === 0) {
      return;
    }
    this.isRetryingDownloads = true;
    this.offlinePackMessage = '';
    let successCount = 0;
    try {
      for (const pack of failed) {
        const ok = await this.contentPackService.retrySubjectPack(pack.courseId, pack.subjectId);
        if (ok) successCount++;
        await this.loadDownloadedPacks();
      }
      if (successCount > 0) {
        this.showOfflinePackMessage(`Retried ${failed.length} pack(s); ${successCount} succeeded`, 'success');
      } else {
        this.showOfflinePackMessage(`Retried ${failed.length} pack(s); none succeeded — check error details`, 'error');
      }
    } catch (error) {
      LoggerService.error('Error retrying failed packs:', error);
      this.showOfflinePackMessage('Retry failed', 'error');
    } finally {
      this.isRetryingDownloads = false;
    }
  }

  private labelForState(state: PackState): string {
    switch (state) {
      case 'pending': return 'Queued';
      case 'downloading': return 'Downloading';
      case 'importing': return 'Installing';
      case 'installed': return 'Ready';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  }

  private badgeClassForState(state: PackState): string {
    switch (state) {
      case 'installed': return 'badge bg-success';
      case 'downloading':
      case 'importing': return 'badge bg-primary';
      case 'pending': return 'badge bg-secondary';
      case 'failed': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  }

  private updatePackStatusPolling(): void {
    const inFlight = this.downloadedPacks.some(
      (p) => p.state === 'downloading' || p.state === 'importing' || p.state === 'pending'
    );
    if (inFlight && !this.packStatusTimer) {
      this.packStatusTimer = setInterval(() => this.loadDownloadedPacks(), 2000);
    } else if (!inFlight && this.packStatusTimer) {
      this.stopPackStatusPolling();
    }
  }

  private stopPackStatusPolling(): void {
    if (this.packStatusTimer) {
      clearInterval(this.packStatusTimer);
      this.packStatusTimer = null;
    }
  }

  formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    const decimals = unitIndex === 0 ? 0 : value < 10 ? 1 : 0;
    return `${value.toFixed(decimals)} ${units[unitIndex]}`;
  }

  async onCourseChange() {
    this.selectedSubjectId = null;
    this.subjects = [];
    
    if (!this.selectedCourseId) {
      return;
    }

    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        return;
      }

      const subjects = await this.restService.getSubjectsForCourse(user.sid, this.selectedCourseId);
      if (subjects) {
        this.subjects = subjects;
      }
    } catch (error) {
      LoggerService.error('Error loading subjects:', error);
    }
  }

  async onLearningGoalCourseChange() {
    await this.loadLearningGoal();
  }

  selectTab(tab: SettingsTab) {
    this.activeTab = tab;
  }

  private resolvedReminderTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private applyGoalFieldsFromSettings(settings: Awaited<ReturnType<RestService['getCourseGoalSettings']>>) {
    const savedGoal = settings?.goal;
    this.weeklyTrainingDays = savedGoal?.weeklyTrainingDays ?? 7;
    this.includeWeekends = (savedGoal?.includeWeekends ?? 1) === 1;
    this.preferredSessionSize = savedGoal?.preferredSessionSize ?? settings?.goalSetup?.preferredSessionSize ?? 20;
    this.dailyMinutesTarget = savedGoal?.dailyMinutesTarget ?? null;
    this.reminderTimeLocal = savedGoal?.reminderTimeLocal ?? '';
    this.reminderStyle = (savedGoal?.reminderStyle as 'relaxed' | 'normal' | 'strict') || 'normal';

    const prefs = settings?.notificationPreference;
    this.notificationInApp = prefs?.inAppEnabled ?? true;
    this.notificationPush = false;
    this.notificationEmail = prefs?.emailEnabled ?? false;
    this.notificationLocal = false;
    this.routineReminderEnabled = prefs?.routineReminderEnabled ?? true;
    this.saveReminderEnabled = prefs?.saveReminderEnabled ?? true;
    this.weeklySummaryEnabled = prefs?.weeklySummaryEnabled ?? true;
    this.quietHoursStart = prefs?.quietHoursStart ?? '';
    this.quietHoursEnd = prefs?.quietHoursEnd ?? '';
  }

  private buildCourseGoalRequest(targetDate: string): Parameters<RestService['saveCourseGoal']>[1] {
    return {
      courseId: this.learningGoalCourseId!,
      targetDate,
      leaderboardOptIn: this.leaderboardOptIn,
      weeklyTrainingDays: this.weeklyTrainingDays,
      includeWeekends: this.includeWeekends,
      preferredSessionSize: this.preferredSessionSize,
      dailyMinutesTarget: this.dailyMinutesTarget ?? undefined,
      reminderTimeLocal: this.reminderTimeLocal || null,
      reminderStyle: this.reminderStyle,
      reminderTimezone: this.resolvedReminderTimezone(),
    };
  }

  async loadLearningGoal() {
    this.goalSetup = null;
    this.goalTargetDate = '';
    this.customGoalDate = '';
    this.selectedGoalPresetId = null;
    if (!this.learningGoalCourseId) {
      return;
    }
    this.isLoadingGoal = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        this.showGoalMessage('User not authenticated', 'error');
        return;
      }
      const settings = await this.restService.getCourseGoalSettings(user.sid, this.learningGoalCourseId);
      this.goalSetup = settings?.goalSetup ?? null;
      this.leaderboardOptIn = settings?.leaderboardOptIn ?? false;
      this.applyGoalFieldsFromSettings(settings);
      const savedDate = settings?.goal?.targetDate || '';
      const matchingPreset = this.goalSetup?.presets.find(p => p.targetDate === savedDate);
      if (matchingPreset) {
        this.selectedGoalPresetId = matchingPreset.id;
        this.customGoalDate = '';
      } else if (savedDate) {
        this.selectedGoalPresetId = null;
        this.customGoalDate = savedDate;
      } else {
        this.selectedGoalPresetId = this.goalSetup?.presets?.[0]?.id ?? null;
        this.customGoalDate = '';
      }
      this.goalTargetDate = this.resolvedGoalTargetDate();
    } catch (error) {
      LoggerService.error('Error loading learning goal:', error);
      this.showGoalMessage('Failed to load learning target', 'error');
    } finally {
      this.isLoadingGoal = false;
    }
  }

  selectGoalPreset(presetId: string) {
    this.selectedGoalPresetId = presetId;
    this.customGoalDate = '';
    this.goalTargetDate = this.resolvedGoalTargetDate();
  }

  onCustomGoalDateChange() {
    this.selectedGoalPresetId = null;
    this.goalTargetDate = this.resolvedGoalTargetDate();
  }

  private resolvedGoalTargetDate(): string {
    if (this.customGoalDate) {
      return this.customGoalDate;
    }
    const preset = this.goalSetup?.presets.find(item => item.id === this.selectedGoalPresetId);
    return preset?.targetDate || '';
  }

  async saveLearningGoal() {
    const targetDate = this.resolvedGoalTargetDate();
    if (!this.learningGoalCourseId || !targetDate || !this.goalSetup) {
      this.showGoalMessage('Choose a course and target date first', 'error');
      return;
    }
    this.isSavingGoal = true;
    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        this.showGoalMessage('User not authenticated', 'error');
        return;
      }
      const saved = await this.restService.saveCourseGoal(user.sid, this.buildCourseGoalRequest(targetDate));
      if (saved) {
        this.showGoalMessage(`Learning target saved: ${saved.dailyQuestionTarget} questions/day`, 'success');
        await this.loadLearningGoal();
      } else {
        this.showGoalMessage('Failed to save learning target', 'error');
      }
    } catch (error) {
      LoggerService.error('Error saving learning goal:', error);
      this.showGoalMessage('Failed to save learning target', 'error');
    } finally {
      this.isSavingGoal = false;
    }
  }

  async saveScheduleSettings() {
    const targetDate = this.resolvedGoalTargetDate() || this.goalSetup?.presets?.[0]?.targetDate || '';
    if (!this.learningGoalCourseId || !targetDate) {
      this.showScheduleMessage('Set a learning target date on the Learning tab first', 'error');
      return;
    }
    this.isSavingSchedule = true;
    try {
      const user = this.authService.getUser();
      if (!user?.sid) {
        this.showScheduleMessage('User not authenticated', 'error');
        return;
      }
      const saved = await this.restService.saveCourseGoal(user.sid, this.buildCourseGoalRequest(targetDate));
      if (saved) {
        this.showScheduleMessage('Training schedule saved', 'success');
        await this.loadLearningGoal();
      } else {
        this.showScheduleMessage('Failed to save training schedule', 'error');
      }
    } catch (error) {
      LoggerService.error('Error saving training schedule:', error);
      this.showScheduleMessage('Failed to save training schedule', 'error');
    } finally {
      this.isSavingSchedule = false;
    }
  }

  async saveReminderSettings() {
    const targetDate = this.resolvedGoalTargetDate() || this.goalSetup?.presets?.[0]?.targetDate || '';
    if (!this.learningGoalCourseId || !targetDate) {
      this.showReminderMessage('Set a learning target date on the Learning tab first', 'error');
      return;
    }
    this.isSavingReminders = true;
    try {
      const user = this.authService.getUser();
      if (!user?.sid) {
        this.showReminderMessage('User not authenticated', 'error');
        return;
      }
      const savedGoal = await this.restService.saveCourseGoal(user.sid, this.buildCourseGoalRequest(targetDate));
      const savedPrefs = await this.restService.saveNotificationPreferences(user.sid, {
        courseId: this.learningGoalCourseId,
        inAppEnabled: this.notificationInApp,
        pushEnabled: false,
        emailEnabled: this.notificationEmail,
        localNotificationEnabled: false,
        routineReminderEnabled: this.routineReminderEnabled,
        saveReminderEnabled: this.saveReminderEnabled,
        weeklySummaryEnabled: this.weeklySummaryEnabled,
        quietHoursStart: this.quietHoursStart || null,
        quietHoursEnd: this.quietHoursEnd || null,
      });
      if (savedGoal && savedPrefs) {
        this.showReminderMessage('Reminder settings saved', 'success');
        await this.loadLearningGoal();
      } else {
        this.showReminderMessage('Failed to save reminder settings', 'error');
      }
    } catch (error) {
      LoggerService.error('Error saving reminder settings:', error);
      this.showReminderMessage('Failed to save reminder settings', 'error');
    } finally {
      this.isSavingReminders = false;
    }
  }

  async saveSettings(silent = false) {
    if (this.settingsForm.invalid) {
      return;
    }

    this.isSaving = true;
    this.message = '';

    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        this.showMessage('User not authenticated', 'error');
        return;
      }

      const formValue = this.settingsForm.value;
      const success = await this.restService.updateUserTestSettings(user.sid, {
        seedId: formValue.seedId,
        shuffleQuestionAnswers: formValue.shuffleQuestionAnswers,
        randomizeQuestions: formValue.randomizeQuestions,
        nextAutomatically: formValue.nextAutomatically
      });

      if (success) {
        this.contentPackService.setAutoDownloadEnabled(!!formValue.autoDownloadLearningPacks);
        this.restService.clearUserTestSettingsCache();
        if (!silent) {
          this.showMessage('Settings saved successfully', 'success');
        }
      } else {
        this.showMessage('Failed to save settings', 'error');
      }
    } catch (error) {
      LoggerService.error('Error saving settings:', error);
      this.showMessage('Failed to save settings', 'error');
    } finally {
      this.isSaving = false;
    }
  }

  requestClearDownloadedPacks() {
    if (this.isClearingDownloads) {
      return;
    }
    this.showClearConfirm = true;
  }

  cancelClearDownloads() {
    this.showClearConfirm = false;
  }

  async confirmClearDownloads() {
    this.showClearConfirm = false;
    this.isClearingDownloads = true;
    this.offlinePackMessage = '';
    try {
      await this.contentPackService.clearDownloadedPacks();
      await this.loadDownloadedPacks();
      this.showOfflinePackMessage('Downloaded offline packs cleared', 'success');
    } catch (error) {
      LoggerService.error('Error clearing learning packs:', error);
      this.showOfflinePackMessage('Failed to clear downloaded offline packs', 'error');
    } finally {
      this.isClearingDownloads = false;
    }
  }

  showResetConfirmDialog() {
    if (!this.selectedCourseId || !this.selectedSubjectId) {
      return;
    }
    this.showResetConfirm = true;
  }

  cancelReset() {
    this.showResetConfirm = false;
  }

  async confirmReset() {
    if (!this.selectedCourseId || !this.selectedSubjectId) {
      return;
    }

    this.isResetting = true;
    this.showResetConfirm = false;

    try {
      const user = this.authService.getUser();
      if (!user || !user.sid) {
        this.showResetMessage('User not authenticated', 'error');
        return;
      }

      const success = await this.restService.resetSubject(user.sid, this.selectedSubjectId);
      if (success) {
        this.showResetMessage('Subject reset successfully', 'success');
        // Reset dropdowns
        this.selectedCourseId = null;
        this.selectedSubjectId = null;
        this.subjects = [];
      } else {
        this.showResetMessage('Failed to reset subject', 'error');
      }
    } catch (error) {
      LoggerService.error('Error resetting subject:', error);
      this.showResetMessage('Failed to reset subject', 'error');
    } finally {
      this.isResetting = false;
    }
  }

  showMessage(message: string, type: 'success' | 'error') {
    this.message = message;
    this.messageType = type;
    setTimeout(() => {
      this.message = '';
    }, 5000);
  }

  showResetMessage(message: string, type: 'success' | 'error') {
    this.resetMessage = message;
    this.resetMessageType = type;
    setTimeout(() => {
      this.resetMessage = '';
    }, 5000);
  }

  showGoalMessage(message: string, type: 'success' | 'error') {
    this.goalMessage = message;
    this.goalMessageType = type;
    setTimeout(() => {
      this.goalMessage = '';
    }, 5000);
  }

  showScheduleMessage(message: string, type: 'success' | 'error') {
    this.scheduleMessage = message;
    this.scheduleMessageType = type;
    setTimeout(() => {
      this.scheduleMessage = '';
    }, 5000);
  }

  showReminderMessage(message: string, type: 'success' | 'error') {
    this.reminderMessage = message;
    this.reminderMessageType = type;
    setTimeout(() => {
      this.reminderMessage = '';
    }, 5000);
  }

  showOfflinePackMessage(message: string, type: 'success' | 'error') {
    this.offlinePackMessage = message;
    this.offlinePackMessageType = type;
    setTimeout(() => {
      this.offlinePackMessage = '';
    }, 5000);
  }

  getSelectedSubjectName(): string {
    if (!this.selectedSubjectId) {
      return '';
    }
    const subject = this.subjects.find(s => s.id === this.selectedSubjectId);
    return subject ? subject.name : '';
  }
}
